import {
  buildLegacyEffectOccurrenceKey,
  getLegacyEffectGlobalOccurrence,
  lookupLegacyEffectData,
} from './effectModel';
import { addLegacyEffect, removeLegacyEffectAt } from './writeAdapters';

const toArray = (value) => (Array.isArray(value) ? value : []);

const LEGACY_EFFECT_FIELDS = {
  buff: {
    temporary: 'buffList',
    permanent: 'permanentBuffs',
    recent: 'recentlyRemovedBuffs',
  },
  debuff: {
    temporary: 'debuffList',
    permanent: 'permanentDebuffs',
    recent: 'recentlyRemovedDebuffs',
  },
};

const getLegacyEffectFieldName = (kind = 'buff', permanent = false) => (
  kind === 'debuff'
    ? (permanent ? LEGACY_EFFECT_FIELDS.debuff.permanent : LEGACY_EFFECT_FIELDS.debuff.temporary)
    : (permanent ? LEGACY_EFFECT_FIELDS.buff.permanent : LEGACY_EFFECT_FIELDS.buff.temporary)
);

const getRecentFieldName = (kind = 'buff') => (
  kind === 'debuff' ? LEGACY_EFFECT_FIELDS.debuff.recent : LEGACY_EFFECT_FIELDS.buff.recent
);

const getPriorArraysForField = (fieldName, char = {}) => {
  switch (fieldName) {
    case 'permanentBuffs':
      return [];
    case 'buffList':
      return [toArray(char.permanentBuffs)];
    case 'permanentDebuffs':
      return [toArray(char.permanentBuffs), toArray(char.buffList)];
    case 'debuffList':
      return [
        toArray(char.permanentBuffs),
        toArray(char.buffList),
        toArray(char.permanentDebuffs),
      ];
    default:
      return [];
  }
};

export const buildRecentlyRemovedLegacyEffectEntry = (
  char = {},
  {
    kind = 'buff',
    permanent = false,
    index = -1,
    text = null,
  } = {},
) => {
  const fieldName = getLegacyEffectFieldName(kind, permanent);
  const currentList = toArray(char[fieldName]);
  const targetIndex = Number.isInteger(index) ? index : -1;
  if (targetIndex < 0 || targetIndex >= currentList.length) return null;

  const targetText = String(text || currentList[targetIndex] || '').trim();
  if (!targetText) return null;

  const occurrence = getLegacyEffectGlobalOccurrence(
    targetText,
    targetIndex,
    currentList,
    ...getPriorArraysForField(fieldName, char),
  );
  const legacyKey = buildLegacyEffectOccurrenceKey(targetText, occurrence);
  const metadata = lookupLegacyEffectData(targetText, occurrence, char.buffData);
  const source = metadata?.source || char.buffSources?.[targetText] || null;
  const note = char.buffNotes?.[targetText] || null;

  return {
    text: targetText,
    kind,
    isPermanent: permanent === true,
    occurrence,
    legacyKey,
    source,
    note,
    metadata: metadata ? { ...metadata } : null,
  };
};

export const removeLegacyEffectToRecent = (
  char = {},
  {
    kind = 'buff',
    permanent = false,
    index = -1,
    text = null,
    limit = 5,
  } = {},
) => {
  const recentEntry = buildRecentlyRemovedLegacyEffectEntry(char, {
    kind,
    permanent,
    index,
    text,
  });
  if (!recentEntry) return char;

  const nextChar = removeLegacyEffectAt(char, {
    kind,
    permanent,
    index,
    text: recentEntry.text,
  });
  const recentField = getRecentFieldName(kind);
  return {
    ...nextChar,
    [recentField]: [recentEntry, ...toArray(nextChar[recentField])].slice(0, limit),
  };
};

export const restoreRecentlyRemovedLegacyEffect = (
  char = {},
  {
    kind = 'buff',
    item = null,
    index = -1,
  } = {},
) => {
  if (!item?.text) return char;
  const recentField = getRecentFieldName(kind);
  const nextChar = addLegacyEffect(char, {
    text: item.text,
    kind,
    permanent: item.isPermanent === true,
    metadata: item.metadata || null,
    buffSource: item.source || null,
    buffNote: item.note || null,
  });

  return {
    ...nextChar,
    [recentField]: toArray(nextChar[recentField]).filter((_, entryIndex) => entryIndex !== index),
  };
};
